import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import {
  COMMAND_PRIORITY_LOW,
  INDENT_CONTENT_COMMAND,
  KEY_TAB_COMMAND,
  OUTDENT_CONTENT_COMMAND,
} from 'lexical';
import { useEffect } from 'react';

/**
 * Plugin that enables Tab/Shift+Tab keyboard shortcuts for indenting/outdenting
 * bullet lists in the Lexical editor.
 *
 * - Tab: Indents the current list item (increases nesting level)
 * - Shift+Tab: Outdents the current list item (decreases nesting level)
 *
 * The plugin uses Lexical's built-in INDENT_CONTENT_COMMAND and OUTDENT_CONTENT_COMMAND
 * which handle all the complexity of list manipulation, edge cases, and ensuring
 * top-level items can't be outdented further.
 */
export function ListIndentPlugin(): null {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    // Register Tab key handler
    const removeTabListener = editor.registerCommand(
      KEY_TAB_COMMAND,
      (event: KeyboardEvent) => {
        const { shiftKey } = event;

        // Prevent default tab behavior
        event.preventDefault();
        event.stopPropagation();

        if (shiftKey) {
          // Shift+Tab: Outdent (decrease nesting)
          editor.dispatchCommand(OUTDENT_CONTENT_COMMAND, undefined);
        } else {
          // Tab: Indent (increase nesting)
          editor.dispatchCommand(INDENT_CONTENT_COMMAND, undefined);
        }

        // Return true to indicate we've handled the command
        return true;
      },
      COMMAND_PRIORITY_LOW,
    );

    // Cleanup on unmount
    return () => {
      removeTabListener();
    };
  }, [editor]);

  return null;
}
